import React, { useState, useEffect } from 'react';
import apiService from '../services/api.service';
import './VisitorForm.css';

const VisitorForm = ({ visitorId, onClose, onSuccess }) => {
  const isEditMode = !!visitorId;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    gender: '',
    address: '',
    country: '',
    state: '',
    city: '',
    pincode: '',
    firm: '',
    exhibition_loc: '',
    aadhar: '',
    status: '0'
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [exhibitionLocations, setExhibitionLocations] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [statesLoading, setStatesLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);

  useEffect(() => {
    // Fetch countries and exhibition locations on mount
    const fetchCountries = async () => {
      try {
        setCountriesLoading(true);
        const response = await apiService.getCountries();
        if (response.data.success) {
          setCountries(response.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch countries:', err);
      } finally {
        setCountriesLoading(false);
      }
    };

    const fetchExhibitionLocations = async () => {
      try {
        setLocationsLoading(true);
        const response = await apiService.getExhibitionLocations();
        if (response.data.success) {
          setExhibitionLocations(response.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch exhibition locations:', err);
      } finally {
        setLocationsLoading(false);
      }
    };
    
    fetchCountries();
    fetchExhibitionLocations();
  }, []);

  useEffect(() => {
    // Fetch states when country changes
    if (formData.country) {
      const fetchStates = async () => {
        try {
          setStatesLoading(true);
          setStates([]);
          setCities([]);
          setFormData(prev => ({ ...prev, state: '', city: '' }));
          
          const response = await apiService.getStates(formData.country);
          if (response.data.success) {
            setStates(response.data.data);
          }
        } catch (err) {
          console.error('Failed to fetch states:', err);
        } finally {
          setStatesLoading(false);
        }
      };
      
      fetchStates();
    }
  }, [formData.country]);

  useEffect(() => {
    // Fetch cities when state changes
    if (formData.state) {
      const fetchCities = async () => {
        try {
          setCitiesLoading(true);
          setCities([]);
          setFormData(prev => ({ ...prev, city: '' }));
          
          const response = await apiService.getCities(formData.state);
          if (response.data.success) {
            setCities(response.data.data);
          }
        } catch (err) {
          console.error('Failed to fetch cities:', err);
        } finally {
          setCitiesLoading(false);
        }
      };
      
      fetchCities();
    }
  }, [formData.state]);

  useEffect(() => {
    if (isEditMode) {
      const fetchVisitor = async () => {
        try {
          setFetchLoading(true);
          setError(null);
          const response = await apiService.getAdminVisitorById(visitorId);
          if (response.data.success) {
            const visitor = response.data.data;
            setFormData({
              name: visitor.name || '',
              email: visitor.email || '',
              mobile: visitor.mobile || '',
              gender: visitor.gender || '',
              address: visitor.address || '',
              country: visitor.country || '',
              state: visitor.state || '',
              city: visitor.city || '',
              pincode: visitor.pincode || '',
              firm: visitor.firm || '',
              exhibition_loc: visitor.exhibition_loc || '',
              aadhar: visitor.aadhar || '',
              status: String(visitor.status) || '0'
            });
          } else {
            setError('Failed to load visitor');
          }
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to load visitor');
          console.error('Visitor fetch error:', err);
        } finally {
          setFetchLoading(false);
        }
      };
      
      fetchVisitor();
    }
  }, [visitorId, isEditMode]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile.trim())) {
      newErrors.mobile = 'Mobile number must be 10 digits';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (formData.pincode && !/^\d{6}$/.test(formData.pincode.trim())) {
      newErrors.pincode = 'Pincode must be 6 digits';
    }

    if (formData.aadhar && !/^\d{12}$/.test(formData.aadhar.trim())) {
      newErrors.aadhar = 'Aadhar must be 12 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = isEditMode
        ? await apiService.updateVisitor(visitorId, formData)
        : await apiService.createVisitor(formData);

      if (response.data.success) {
        onSuccess && onSuccess(isEditMode ? 'Visitor updated successfully!' : 'Visitor created successfully!');
        onClose && onClose();
      } else {
        setError('Failed to save visitor');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save visitor');
      console.error('Visitor save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose && onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content visitor-form-modal">
        <div className="modal-header">
          <h3>{isEditMode ? 'Edit Visitor' : 'Add New Visitor'}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {fetchLoading ? (
          <div className="modal-body">
            <div className="loading">Loading visitor data...</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-error">
                  <strong>Error:</strong> {error}
                </div>
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">Name <span className="required">*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? 'error' : ''}
                    placeholder="Enter full name"
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="mobile">Mobile <span className="required">*</span></label>
                  <input
                    type="text"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    className={errors.mobile ? 'error' : ''}
                    placeholder="10-digit mobile number"
                    maxLength="10"
                  />
                  {errors.mobile && <span className="error-message">{errors.mobile}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="email@example.com"
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="gender">Gender <span className="required">*</span></label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className={errors.gender ? 'error' : ''}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.gender && <span className="error-message">{errors.gender}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="firm">Firm/Organization</label>
                  <input
                    type="text"
                    id="firm"
                    name="firm"
                    value={formData.firm}
                    onChange={handleChange}
                    placeholder="Company or organization name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="aadhar">Aadhar Number</label>
                  <input
                    type="text"
                    id="aadhar"
                    name="aadhar"
                    value={formData.aadhar}
                    onChange={handleChange}
                    className={errors.aadhar ? 'error' : ''}
                    placeholder="12-digit Aadhar number"
                    maxLength="12"
                  />
                  {errors.aadhar && <span className="error-message">{errors.aadhar}</span>}
                </div>

                <div className="form-group full-width">
                  <label htmlFor="address">Address</label>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Full address"
                    rows="2"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="country">Country</label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    disabled={countriesLoading}
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <select
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    disabled={!formData.country || statesLoading}
                  >
                    <option value="">Select State</option>
                    {states.map(state => (
                      <option key={state.id} value={state.id}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <select
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    disabled={!formData.state || citiesLoading}
                  >
                    <option value="">Select City</option>
                    {cities.map(city => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="pincode">Pincode</label>
                  <input
                    type="text"
                    id="pincode"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    className={errors.pincode ? 'error' : ''}
                    placeholder="6-digit pincode"
                    maxLength="6"
                  />
                  {errors.pincode && <span className="error-message">{errors.pincode}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="exhibition_loc">Exhibition Location</label>
                  <select
                    id="exhibition_loc"
                    name="exhibition_loc"
                    value={formData.exhibition_loc}
                    onChange={handleChange}
                    disabled={locationsLoading}
                  >
                    <option value="">Select Exhibition Location</option>
                    {exhibitionLocations.map((loc, idx) => (
                      <option key={idx} value={loc.location}>
                        {loc.location}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="0">Pending</option>
                    <option value="1">Active</option>
                    <option value="2">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : (isEditMode ? 'Update Visitor' : 'Create Visitor')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default VisitorForm;
